const DEFAULT_API_BASE = '/api';
const TOKEN_KEY = 'auth_token';

// Dev bypass: check for ?dev=true in URL (only works in development)
const isDevBypass =
  import.meta.env.DEV && new URLSearchParams(window.location.search).get('dev') === 'true';

export function getApiBaseUrl() {
  const base = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (!base) return DEFAULT_API_BASE;
  return base.replace(/\/$/, '');
}

export function apiUrl(path: string) {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  let url = `${getApiBaseUrl()}${normalized}`;

  // Add dev=true query param if in dev bypass mode
  if (isDevBypass) {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}dev=true`;
  }

  return url;
}

// Authenticated fetch wrapper - automatically includes auth token
export async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(apiUrl(path), {
    ...options,
    headers,
  });
}

// ==================== User Types ====================

export interface User {
  id: number;
  email: string;
  name: string | null;
  picture: string | null;
  username: string | null;
}

export interface PublicUser {
  id: number;
  username: string | null;
  name: string | null;
  picture: string | null;
}

export interface Friend extends PublicUser {
  friendship_id: number;
}

export interface FriendRequest {
  id: number;
  from_user: PublicUser;
  created_at: string;
}

export interface Notification {
  id: number;
  type: 'friend_request' | 'playlist_shared' | 'playlist_movie_added';
  from_user: PublicUser;
  reference_id: number | null;
  metadata: Record<string, unknown> | null;
  read: boolean;
  created_at: string;
}

export interface FeatureUpdate {
  id: string;
  type: 'feature_update';
  title: string;
  description: string;
  read: boolean;
  created_at: string;
}

export interface PlaylistShare {
  id: number;
  user: PublicUser;
  permission: 'read' | 'edit';
  created_at: string;
}

// ==================== Playlist Types ====================

export interface SharedByInfo {
  user_id: number;
  username: string | null;
  name: string | null;
}

export type PlaylistType = 'want_to_watch' | 'watched';

export interface Playlist {
  id: number;
  user_id: number;
  name: string;
  prompt: string | null;
  type: PlaylistType | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string | null;
  progress_found: number;
  progress_approved: number;
  movie_count?: number; // Actual movie count (included when listing playlists)
  created_at: string;
  completed_at: string | null;
  is_shared?: boolean; // Whether this playlist is owned or shared
  shared_by?: SharedByInfo; // Present when playlist is shared with user
  permission?: 'owner' | 'read' | 'edit'; // User's permission level
}

export interface PlaylistMovie {
  id: number;
  playlist_id: number;
  tmdb_id: number;
  title: string | null;
  original_title: string | null;
  year: number | null;
  poster_url: string | null;
  overview: string | null;
  runtime: number | null;
  imdb_rating: string | null;
  rotten_tomatoes_rating: string | null;
  metacritic_rating: string | null;
  relevance_score: number | null;
  original_language: string | null;
  genres: string | null; // JSON string of genre names
  added_at: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PlaylistFilterOptions {
  languages: string[];
  yearRange: { min: number; max: number } | null;
  genres: string[];
}

export interface PlaylistWithMovies {
  playlist: Playlist;
  movies: PlaylistMovie[];
  pagination: PaginationInfo;
  filterOptions: PlaylistFilterOptions;
  watched_count: number;
  friends_watched_count: number;
  friends_watched_movie_ids: number[];
  total_count: number;
  permission: 'owner' | 'read' | 'edit';
  shared_by: SharedByInfo | null;
}

export interface GetPlaylistParams {
  page?: number;
  limit?: number;
  sortBy?: 'year' | 'title' | 'watched_at' | 'added_at';
  sortOrder?: 'asc' | 'desc';
  language?: string;
  yearMin?: number;
  yearMax?: number;
  genre?: string;
}

export interface FriendWhoWatched {
  id: number;
  name: string | null;
  username: string | null;
  picture: string | null;
}

export interface PlaylistsResponse {
  playlists: Playlist[];
  shared_playlists: Playlist[];
}

// ==================== Playlist API Functions ====================

/**
 * Get all playlists for the current user (owned + shared)
 */
export async function getPlaylists(): Promise<PlaylistsResponse> {
  const response = await apiFetch('/playlists');
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to load playlists' }));
    throw new Error(error.error || 'Failed to load playlists');
  }
  return response.json();
}

/**
 * Create a new playlist
 * For AI-generated playlists, provide a prompt (name is optional)
 * For manual playlists, provide a name (prompt is optional)
 */
/**
 * Custom error class for validation errors with suggestions
 */
export class ValidationError extends Error {
  suggestions?: string[];

  constructor(message: string, suggestions?: string[]) {
    super(message);
    this.name = 'ValidationError';
    this.suggestions = suggestions;
  }
}

export async function createPlaylist(options: {
  prompt?: string;
  name?: string;
}): Promise<Playlist> {
  const response = await apiFetch('/playlists', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create playlist' }));

    // Check if this is a validation error with suggestions
    if (error.validationError && error.suggestions) {
      throw new ValidationError(error.error || 'Invalid prompt', error.suggestions);
    }

    throw new Error(error.error || 'Failed to create playlist');
  }

  const data = await response.json();
  return data.playlist;
}

/**
 * Create a playlist by importing movies from an IMDB CSV export
 * Returns the playlist immediately; import happens in background with SSE progress updates
 */
export async function importPlaylistFromCsv(options: {
  name: string;
  csvContent: string;
  markAsWatched?: boolean;
}): Promise<Playlist> {
  const response = await apiFetch('/playlists/import-csv', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to import playlist' }));
    throw new Error(error.error || 'Failed to import playlist from CSV');
  }

  const data = await response.json();
  return data.playlist;
}

export type UpdatePlaylistMode = 'reset' | 'keep';

/**
 * Update a playlist's name and/or prompt
 * mode: 'reset' clears all movies and reprocesses, 'keep' keeps existing movies
 */
export async function updatePlaylist(
  id: number,
  options: { name?: string; prompt?: string; mode?: UpdatePlaylistMode }
): Promise<Playlist> {
  const response = await apiFetch(`/playlists/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update playlist' }));
    throw new Error(error.error || 'Failed to update playlist');
  }

  const data = await response.json();
  return data.playlist;
}

/**
 * Get a playlist with its movies
 * Supports pagination, sorting, and filtering via params
 */
export async function getPlaylist(
  id: number,
  params: GetPlaylistParams = {}
): Promise<PlaylistWithMovies> {
  // Build query string from params
  const queryParams = new URLSearchParams();
  if (params.page !== undefined) queryParams.set('page', params.page.toString());
  if (params.limit !== undefined) queryParams.set('limit', params.limit.toString());
  if (params.sortBy !== undefined) queryParams.set('sortBy', params.sortBy);
  if (params.sortOrder !== undefined) queryParams.set('sortOrder', params.sortOrder);
  if (params.language !== undefined) queryParams.set('language', params.language);
  if (params.yearMin !== undefined) queryParams.set('yearMin', params.yearMin.toString());
  if (params.yearMax !== undefined) queryParams.set('yearMax', params.yearMax.toString());
  if (params.genre !== undefined) queryParams.set('genre', params.genre);

  const queryString = queryParams.toString();
  const url = queryString ? `/playlists/${id}?${queryString}` : `/playlists/${id}`;

  const response = await apiFetch(url);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to load playlist' }));
    throw new Error(error.error || 'Failed to load playlist');
  }
  return response.json();
}

/**
 * Delete a playlist
 */
export async function deletePlaylist(id: number): Promise<void> {
  const response = await apiFetch(`/playlists/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete playlist' }));
    throw new Error(error.error || 'Failed to delete playlist');
  }
}

/**
 * Remove a movie from a playlist
 */
export async function removeMovieFromPlaylist(playlistId: number, tmdbId: number): Promise<void> {
  const response = await apiFetch(`/playlists/${playlistId}/movies/${tmdbId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to remove movie' }));
    throw new Error(error.error || 'Failed to remove movie');
  }
}

// ==================== Watched Movies API Functions ====================

/**
 * Get all watched movie IDs for the current user
 */
export async function getWatchedMovies(): Promise<number[]> {
  const response = await apiFetch('/movies/watched');
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get watched movies' }));
    throw new Error(error.error || 'Failed to get watched movies');
  }
  const data = await response.json();
  return data.watched;
}

/**
 * Mark a movie as watched
 */
export async function markMovieWatched(tmdbId: number): Promise<void> {
  const response = await apiFetch(`/movies/${tmdbId}/watched`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to mark movie as watched' }));
    throw new Error(error.error || 'Failed to mark movie as watched');
  }
}

/**
 * Unmark a movie as watched
 */
export async function unmarkMovieWatched(tmdbId: number): Promise<void> {
  const response = await apiFetch(`/movies/${tmdbId}/watched`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Failed to unmark movie as watched' }));
    throw new Error(error.error || 'Failed to unmark movie as watched');
  }
}

// ==================== Movie Details Types ====================

export interface Movie {
  tmdb_id: number;
  title: string;
  original_title: string | null;
  release_date: string | null;
  year: number | null;
  overview: string | null;
  original_language: string | null;
  runtime: number | null;
  genres: Array<{ id: number; name: string }>;
  production_countries: Array<{ iso_3166_1: string; name: string }>;
  imdb_id: string | null;
  imdb_rating: string | null;
  imdb_votes: string | null;
  rotten_tomatoes_rating: string | null;
  metacritic_rating: string | null;
  poster_url: string | null;
  backdrop_url: string | null;
}

export interface MovieCredits {
  director: { id: number; name: string } | null;
  writers: Array<{ id: number; name: string; job: string }>;
  cast: Array<{ id: number; name: string; character: string; profile_url: string | null }>;
}

export interface MovieVideo {
  id: string;
  key: string;
  name: string;
  type: string;
  official: boolean;
  published_at: string;
}

// ==================== Person Credits Types ====================

export interface PersonInfo {
  id: number;
  name: string;
  profile_url: string | null;
  known_for_department: string | null;
}

export interface PersonActingCredit {
  tmdb_id: number;
  title: string;
  original_title: string | null;
  year: number | null;
  poster_url: string | null;
  character: string | null;
  imdb_rating: string | null;
  rotten_tomatoes_rating: string | null;
  metacritic_rating: string | null;
  is_watched: boolean;
  is_want_to_watch: boolean;
  is_friends_watched: boolean;
}

export interface PersonCrewCredit {
  tmdb_id: number;
  title: string;
  original_title: string | null;
  year: number | null;
  poster_url: string | null;
  job?: string | null;
  imdb_rating: string | null;
  rotten_tomatoes_rating: string | null;
  metacritic_rating: string | null;
  is_watched: boolean;
  is_want_to_watch: boolean;
  is_friends_watched: boolean;
}

export interface PersonCredits {
  person: PersonInfo;
  acting: PersonActingCredit[];
  directing: PersonCrewCredit[];
  writing: PersonCrewCredit[];
}

/**
 * Get all movie credits for a person (actor/director/writer)
 */
export async function getPersonCredits(personId: number): Promise<PersonCredits> {
  const response = await apiFetch(`/movies/person/${personId}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to load person credits' }));
    throw new Error(error.error || 'Failed to load person credits');
  }
  return response.json();
}

/**
 * Get movie details by TMDB ID
 */
export async function getMovie(tmdbId: number): Promise<Movie> {
  const response = await apiFetch(`/movies/${tmdbId}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to load movie' }));
    throw new Error(error.error || 'Failed to load movie');
  }
  return response.json();
}

/**
 * Get movie credits (cast & crew) from TMDB
 */
export async function getMovieCredits(tmdbId: number): Promise<MovieCredits> {
  const response = await apiFetch(`/movies/${tmdbId}/credits`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to load credits' }));
    throw new Error(error.error || 'Failed to load credits');
  }
  return response.json();
}

/**
 * Get movie videos (trailers, teasers) from TMDB
 */
export async function getMovieVideos(tmdbId: number): Promise<MovieVideo[]> {
  const response = await apiFetch(`/movies/${tmdbId}/videos`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to load videos' }));
    throw new Error(error.error || 'Failed to load videos');
  }
  const data = await response.json();
  return data.videos;
}

/**
 * Get friends who have watched a specific movie
 */
export async function getFriendsWhoWatchedMovie(tmdbId: number): Promise<FriendWhoWatched[]> {
  const response = await apiFetch(`/movies/${tmdbId}/friends-watched`);
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Failed to get friends who watched' }));
    throw new Error(error.error || 'Failed to get friends who watched');
  }
  const data = await response.json();
  return data.friends;
}

// ==================== Watch Providers ====================

export interface WatchProvider {
  provider_id: number;
  provider_name: string;
  logo_url: string;
}

export interface WatchProviders {
  flatrate?: WatchProvider[];
  rent?: WatchProvider[];
  buy?: WatchProvider[];
  link?: string;
}

/**
 * Get streaming watch providers for a movie by country (JustWatch data via TMDB)
 */
export async function getWatchProviders(
  tmdbId: number,
  country?: string
): Promise<WatchProviders | null> {
  const params = country ? `?country=${encodeURIComponent(country)}` : '';
  const response = await apiFetch(`/movies/${tmdbId}/watch-providers${params}`);
  if (!response.ok) {
    return null;
  }
  const data = await response.json();
  return data.providers ?? null;
}

// ==================== TMDB Search Types ====================

export interface TMDBSearchResult {
  tmdb_id: number;
  title: string;
  original_title: string | null;
  year: number | null;
  poster_url: string | null;
  overview: string;
}

/**
 * Search TMDB for movies
 */
export async function searchTMDB(query: string): Promise<TMDBSearchResult[]> {
  const response = await apiFetch(`/playlists/search-tmdb?q=${encodeURIComponent(query)}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to search movies' }));
    throw new Error(error.error || 'Failed to search movies');
  }

  const data = await response.json();
  return data.movies;
}

/**
 * Add a movie to a playlist by TMDB ID
 */
export async function addMovieToPlaylist(
  playlistId: number,
  tmdbId: number
): Promise<PlaylistMovie> {
  const response = await apiFetch(`/playlists/${playlistId}/movies`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tmdbId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to add movie' }));
    throw new Error(error.error || 'Failed to add movie');
  }

  const data = await response.json();
  return data.movie;
}

/**
 * Poll playlist status updates. Returns an AbortController so the caller can
 * cancel polling on unmount. Uses the same strategy as the iOS client: start at
 * 2 s, settle to 3 s during processing, exponential back-off on errors (max 10 s),
 * and stop automatically on terminal states (completed / failed).
 */
export function pollPlaylistStatus(
  id: number,
  onUpdate: (data: { status: string; found: number; approved: number; message?: string }) => void
): AbortController {
  const controller = new AbortController();

  (async () => {
    let interval = 2000;

    while (!controller.signal.aborted) {
      await new Promise((r) => setTimeout(r, interval));
      if (controller.signal.aborted) break;

      try {
        const data = await apiFetch(`/playlists/${id}/poll`);
        const json = await data.json();

        onUpdate({
          status: json.status,
          found: json.progress_found ?? 0,
          approved: json.progress_approved ?? 0,
          message: json.message ?? json.error_message,
        });

        if (json.status === 'completed' || json.status === 'failed') {
          break;
        }

        interval = 3000;
      } catch {
        interval = Math.min(interval * 2, 10000);
      }
    }
  })();

  return controller;
}

// ==================== User API Functions ====================

/**
 * Set or update username
 */
export async function setUsername(username: string): Promise<User> {
  const response = await apiFetch('/users/me/username', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to set username' }));
    throw new Error(error.error || 'Failed to set username');
  }

  const data = await response.json();
  return data.user;
}

/**
 * Check if username is available
 */
export async function checkUsernameAvailability(
  username: string
): Promise<{ available: boolean; error?: string }> {
  const response = await apiFetch(`/users/check-username/${encodeURIComponent(username)}`);

  if (!response.ok) {
    return { available: false, error: 'Failed to check username' };
  }

  return response.json();
}

/**
 * Search users by username
 */
export async function searchUsers(query: string): Promise<PublicUser[]> {
  const response = await apiFetch(`/users/search?q=${encodeURIComponent(query)}`);

  if (!response.ok) {
    throw new Error('Failed to search users');
  }

  const data = await response.json();
  return data.users;
}

// ==================== Friends API Functions ====================

/**
 * Get list of friends
 */
export async function getFriends(): Promise<Friend[]> {
  const response = await apiFetch('/friends');

  if (!response.ok) {
    throw new Error('Failed to get friends');
  }

  const data = await response.json();
  return data.friends;
}

/**
 * Get pending friend requests
 */
export async function getFriendRequests(): Promise<FriendRequest[]> {
  const response = await apiFetch('/friends/requests');

  if (!response.ok) {
    throw new Error('Failed to get friend requests');
  }

  const data = await response.json();
  return data.requests;
}

/**
 * Send friend request by username
 */
export async function sendFriendRequest(username: string): Promise<void> {
  const response = await apiFetch('/friends', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to send friend request' }));
    throw new Error(error.error || 'Failed to send friend request');
  }
}

/**
 * Accept friend request
 */
export async function acceptFriendRequest(requestId: number): Promise<void> {
  const response = await apiFetch(`/friends/${requestId}/accept`, {
    method: 'PUT',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to accept friend request' }));
    throw new Error(error.error || 'Failed to accept friend request');
  }
}

/**
 * Reject friend request
 */
export async function rejectFriendRequest(requestId: number): Promise<void> {
  const response = await apiFetch(`/friends/${requestId}/reject`, {
    method: 'PUT',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to reject friend request' }));
    throw new Error(error.error || 'Failed to reject friend request');
  }
}

/**
 * Remove friend
 */
export async function removeFriend(friendshipId: number): Promise<void> {
  const response = await apiFetch(`/friends/${friendshipId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to remove friend' }));
    throw new Error(error.error || 'Failed to remove friend');
  }
}

// ==================== Playlist Sharing API Functions ====================

/**
 * Get shares for a playlist
 */
export async function getPlaylistShares(playlistId: number): Promise<PlaylistShare[]> {
  const response = await apiFetch(`/playlists/${playlistId}/shares`);

  if (!response.ok) {
    throw new Error('Failed to get playlist shares');
  }

  const data = await response.json();
  return data.shares;
}

/**
 * Share playlist with a friend
 */
export async function sharePlaylist(
  playlistId: number,
  friendId: number,
  permission: 'read' | 'edit'
): Promise<void> {
  const response = await apiFetch(`/playlists/${playlistId}/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ friendId, permission }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to share playlist' }));
    throw new Error(error.error || 'Failed to share playlist');
  }
}

/**
 * Update share permission
 */
export async function updatePlaylistSharePermission(
  playlistId: number,
  userId: number,
  permission: 'read' | 'edit'
): Promise<void> {
  const response = await apiFetch(`/playlists/${playlistId}/share/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ permission }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update share' }));
    throw new Error(error.error || 'Failed to update share');
  }
}

/**
 * Remove share
 */
export async function removePlaylistShare(playlistId: number, userId: number): Promise<void> {
  const response = await apiFetch(`/playlists/${playlistId}/share/${userId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to remove share' }));
    throw new Error(error.error || 'Failed to remove share');
  }
}

// ==================== Inbox API Functions ====================

/**
 * Get notifications
 */
export async function getNotifications(
  unreadOnly: boolean = false
): Promise<{ notifications: Notification[]; feature_updates: FeatureUpdate[] }> {
  const params = new URLSearchParams({ platform: 'web' });
  if (unreadOnly) params.set('unread', 'true');
  const url = `/inbox?${params.toString()}`;
  const response = await apiFetch(url);

  if (!response.ok) {
    throw new Error('Failed to get notifications');
  }

  const data = await response.json();
  return {
    notifications: data.notifications,
    feature_updates: data.feature_updates || [],
  };
}

/**
 * Get unread notification count
 */
export async function getUnreadNotificationCount(): Promise<number> {
  const response = await apiFetch('/inbox/count?platform=web');

  if (!response.ok) {
    throw new Error('Failed to get notification count');
  }

  const data = await response.json();
  return data.unread_count;
}

/**
 * Mark notification as read
 */
export async function markNotificationRead(notificationId: number): Promise<void> {
  const response = await apiFetch(`/inbox/${notificationId}/read`, {
    method: 'PUT',
  });

  if (!response.ok) {
    throw new Error('Failed to mark notification as read');
  }
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsRead(): Promise<void> {
  const response = await apiFetch('/inbox/read-all', {
    method: 'PUT',
  });

  if (!response.ok) {
    throw new Error('Failed to mark all notifications as read');
  }
}

/**
 * Delete notification
 */
export async function deleteNotification(notificationId: number): Promise<void> {
  const response = await apiFetch(`/inbox/${notificationId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete notification');
  }
}

/**
 * Dismiss a feature update
 */
export async function dismissFeatureUpdate(updateId: string): Promise<void> {
  const response = await apiFetch(`/inbox/feature-update/${updateId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to dismiss feature update');
  }
}

// ==================== Admin API Functions ====================

export interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  username: string | null;
  picture: string | null;
  created_at: string;
  playlist_count: number;
  movie_count: number;
}

/**
 * Get all users (admin only)
 */
export async function getAdminUsers(): Promise<AdminUser[]> {
  const response = await apiFetch('/admin/users');

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin access required');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to get users' }));
    throw new Error(error.error || 'Failed to get users');
  }

  const data = await response.json();
  return data.users;
}

export interface AdminUserDetailPlaylist {
  id: number;
  name: string;
  prompt: string | null;
  type: string | null;
  status: string;
  movie_count: number;
  created_at: string;
}

export interface AdminUserPlatform {
  platform: 'web' | 'ios';
  device_model: string | null;
  os_version: string | null;
  app_version: string | null;
  locale: string | null;
  last_seen_at: string;
  created_at: string;
}

export interface AdminUserDetail {
  user: AdminUser;
  stats: {
    playlist_count: number;
    movie_count: number;
    watched_count: number;
    friend_count: number;
  };
  platforms: AdminUserPlatform[];
  playlists: AdminUserDetailPlaylist[];
}

/**
 * Get detailed info for a single user (admin only)
 */
export async function getAdminUserDetail(userId: number): Promise<AdminUserDetail> {
  const response = await apiFetch(`/admin/users/${userId}`);

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin access required');
    }
    if (response.status === 404) {
      throw new Error('User not found');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to get user detail' }));
    throw new Error(error.error || 'Failed to get user detail');
  }

  return response.json();
}

// ==================== Feedback Types ====================

export type FeedbackCategory = 'bug' | 'feature' | 'question' | 'other';

export interface AdminFeedbackItem {
  id: number;
  user_id: number;
  category: FeedbackCategory;
  message: string;
  app_version: string | null;
  platform: string | null;
  device_info: string | null;
  created_at: string;
  user_name: string | null;
  user_email: string;
  user_picture: string | null;
}

/**
 * Get all feedback entries (admin only)
 */
export async function getAdminFeedback(category?: FeedbackCategory): Promise<AdminFeedbackItem[]> {
  const params = category ? `?category=${category}` : '';
  const response = await apiFetch(`/admin/feedback${params}`);

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin access required');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to get feedback' }));
    throw new Error(error.error || 'Failed to get feedback');
  }

  const data = await response.json();
  return data.feedback;
}

export interface BackfillResult {
  status: 'completed' | 'stopped';
  total: number;
  processed: number;
  updated: number;
  skipped: number;
  errors: number;
  rateLimited: boolean;
}

/**
 * Start OMDB metadata backfill (admin only)
 */
export async function startOmdbBackfill(batchSize: number = 50): Promise<BackfillResult> {
  const response = await apiFetch(`/admin/backfill/omdb?batchSize=${batchSize}`, {
    method: 'POST',
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin access required');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to start backfill' }));
    throw new Error(error.error || 'Failed to start backfill');
  }

  return response.json();
}

// ==================== Admin Movie Search & Debug ====================

export interface AdminMovieSearchResult {
  tmdb_id: number;
  title: string;
  year: number | null;
  poster_url: string | null;
}

export interface AdminMovieDebug {
  db: {
    movie_cache: Record<string, unknown> | null;
    playlist_movies: Array<Record<string, unknown>>;
  };
  tmdb_raw: Record<string, unknown>;
  omdb_raw: Record<string, unknown> | null;
}

export async function adminSearchMovies(query: string): Promise<AdminMovieSearchResult[]> {
  const response = await apiFetch(`/admin/movies/search?q=${encodeURIComponent(query)}`);
  if (!response.ok) throw new Error('Failed to search movies');
  const data = await response.json();
  return data.results;
}

export async function adminGetMovieDebug(tmdbId: number): Promise<AdminMovieDebug> {
  const response = await apiFetch(`/admin/movies/${tmdbId}/debug`);
  if (!response.ok) throw new Error('Failed to get movie debug data');
  return response.json();
}

// ==================== Admin Stats API Types & Functions ====================

export interface AdminStatsOverview {
  total_users: number;
  total_playlists: number;
  total_watched: number;
  active_7d: number;
  active_30d: number;
  pro_users: number;
  friendships: number;
  feedback: number;
}

export interface AdminStatsGrowth {
  signups: Array<{ date: string; count: number }>;
  playlists: Array<{ date: string; count: number }>;
  watched: Array<{ date: string; count: number }>;
}

export interface AdminStatsPlatforms {
  platforms: Array<{ platform: string; count: number }>;
  device_models: Array<{ device_model: string; count: number }>;
  app_versions: Array<{ app_version: string; count: number }>;
}

export interface AdminLeaderboardUser {
  id: number;
  name: string | null;
  email: string;
  username: string | null;
  count: number;
}

export interface AdminStatsEngagement {
  avg_playlists_per_user: number;
  avg_watched_per_user: number;
  top_watchers: AdminLeaderboardUser[];
  top_playlist_creators: AdminLeaderboardUser[];
}

export interface AdminStatsContent {
  most_watched: Array<{
    tmdb_id: number;
    title: string | null;
    poster_url: string | null;
    year: number | null;
    watch_count: number;
  }>;
  playlist_types: { ai_generated: number; manual: number };
  playlist_statuses: Array<{ status: string; count: number }>;
}

export interface AdminStatsRecent {
  signups: Array<{
    id: number;
    email: string;
    name: string | null;
    username: string | null;
    picture: string | null;
    created_at: string;
  }>;
  playlists: Array<{
    id: number;
    name: string;
    prompt: string | null;
    status: string;
    created_at: string;
    user_name: string | null;
    user_email: string;
  }>;
  feedback: Array<{
    id: number;
    category: string;
    message: string;
    platform: string | null;
    created_at: string;
    user_name: string | null;
    user_email: string;
  }>;
}

export async function getAdminStatsOverview(): Promise<AdminStatsOverview> {
  const response = await apiFetch('/admin/stats/overview');
  if (!response.ok) throw new Error('Failed to get stats overview');
  return response.json();
}

export async function getAdminStatsGrowth(days: number = 30): Promise<AdminStatsGrowth> {
  const response = await apiFetch(`/admin/stats/growth?days=${days}`);
  if (!response.ok) throw new Error('Failed to get growth stats');
  return response.json();
}

export async function getAdminStatsPlatforms(): Promise<AdminStatsPlatforms> {
  const response = await apiFetch('/admin/stats/platforms');
  if (!response.ok) throw new Error('Failed to get platform stats');
  return response.json();
}

export async function getAdminStatsEngagement(): Promise<AdminStatsEngagement> {
  const response = await apiFetch('/admin/stats/engagement');
  if (!response.ok) throw new Error('Failed to get engagement stats');
  return response.json();
}

export async function getAdminStatsContent(): Promise<AdminStatsContent> {
  const response = await apiFetch('/admin/stats/content');
  if (!response.ok) throw new Error('Failed to get content stats');
  return response.json();
}

export async function getAdminStatsRecent(): Promise<AdminStatsRecent> {
  const response = await apiFetch('/admin/stats/recent');
  if (!response.ok) throw new Error('Failed to get recent activity');
  return response.json();
}

// ==================== Stats API Types & Functions ====================

export interface WatchedStats {
  total_movies: number;
  total_cached: number;
  total_runtime_minutes: number;
  genres: Array<{ name: string; count: number }>;
  decades: Array<{ name: string; count: number }>;
  years: Array<{ year: number; count: number }>;
  top_directors: Array<{ name: string; count: number }>;
  top_actors: Array<{ name: string; count: number }>;
  ratings: {
    average_tmdb: number | null;
    average_imdb: number | null;
    distribution: Array<{ name: string; count: number }>;
  };
  languages: Array<{ name: string; count: number }>;
  oldest_movie: { title: string; year: number } | null;
  newest_movie: { title: string; year: number } | null;
}

export interface WatchedMovieItem {
  tmdb_id: number;
  title: string | null;
  original_title: string | null;
  year: number | null;
  poster_url: string | null;
  runtime: number | null;
  genres: string[];
  original_language: string | null;
  vote_average: number | null;
  imdb_rating: string | null;
  directors: string[];
  watched_at: string;
}

export interface WatchedMoviesListResponse {
  movies: WatchedMovieItem[];
  pagination: PaginationInfo;
  available_filters: {
    genres: string[];
    decades: string[];
    languages: string[];
  };
}

export interface GetWatchedMoviesParams {
  page?: number;
  limit?: number;
  sort?: 'watched_at' | 'title' | 'year' | 'rating';
  order?: 'asc' | 'desc';
  genre?: string;
  decade?: string;
  language?: string;
}

/**
 * Get comprehensive watched movie stats
 */
export async function getWatchedStats(): Promise<WatchedStats> {
  const response = await apiFetch('/stats');
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get stats' }));
    throw new Error(error.error || 'Failed to get stats');
  }
  return response.json();
}

/**
 * Get paginated list of watched movies with details
 */
export async function getWatchedMoviesList(
  params: GetWatchedMoviesParams = {}
): Promise<WatchedMoviesListResponse> {
  const queryParams = new URLSearchParams();
  if (params.page !== undefined) queryParams.set('page', params.page.toString());
  if (params.limit !== undefined) queryParams.set('limit', params.limit.toString());
  if (params.sort) queryParams.set('sort', params.sort);
  if (params.order) queryParams.set('order', params.order);
  if (params.genre) queryParams.set('genre', params.genre);
  if (params.decade) queryParams.set('decade', params.decade);
  if (params.language) queryParams.set('language', params.language);

  const queryString = queryParams.toString();
  const url = queryString ? `/stats/movies?${queryString}` : '/stats/movies';

  const response = await apiFetch(url);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get watched movies' }));
    throw new Error(error.error || 'Failed to get watched movies');
  }
  return response.json();
}

// ==================== Friend Stats API Functions ====================

export interface FriendStats {
  total_movies: number;
  total_runtime_minutes: number;
  ratings: { average_tmdb: number | null; average_imdb: number | null };
  oldest_movie: { title: string; year: number } | null;
  newest_movie: { title: string; year: number } | null;
  top_directors: Array<{ name: string; count: number }>;
  top_actors: Array<{ name: string; count: number }>;
  friend: { id: number; username: string; name: string | null; picture: string | null };
}

/**
 * Get simplified stats for a friend
 */
export async function getFriendStats(friendId: number): Promise<FriendStats> {
  const response = await apiFetch(`/friends/${friendId}/stats`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get friend stats' }));
    throw new Error(error.error || 'Failed to get friend stats');
  }
  return response.json();
}

/**
 * Get paginated list of a friend's watched movies with details
 */
export async function getFriendWatchedMovies(
  friendId: number,
  params: GetWatchedMoviesParams = {}
): Promise<WatchedMoviesListResponse> {
  const queryParams = new URLSearchParams();
  if (params.page !== undefined) queryParams.set('page', params.page.toString());
  if (params.limit !== undefined) queryParams.set('limit', params.limit.toString());
  if (params.sort) queryParams.set('sort', params.sort);
  if (params.order) queryParams.set('order', params.order);
  if (params.genre) queryParams.set('genre', params.genre);
  if (params.language) queryParams.set('language', params.language);

  const query = queryParams.toString();
  const response = await apiFetch(`/friends/${friendId}/movies${query ? `?${query}` : ''}`);
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Failed to get friend's watched movies" }));
    throw new Error(error.error || "Failed to get friend's watched movies");
  }
  return response.json();
}

// ==================== Movie Discovery API Functions ====================

export interface DiscoveryMovie {
  tmdb_id: number;
  title: string;
  original_title: string | null;
  year: number | null;
  overview: string | null;
  poster_url: string | null;
  vote_average: number;
  imdb_rating: string | null;
  rotten_tomatoes_rating: string | null;
  metacritic_rating: string | null;
  is_watched: boolean;
  is_want_to_watch: boolean;
  is_friends_watched: boolean;
}

export interface DiscoveryResponse {
  results: DiscoveryMovie[];
  page: number;
  totalPages: number;
  totalResults: number;
}

export interface PersonSearchResponse extends DiscoveryResponse {
  person: { id: number; name: string; profile_url: string | null } | null;
}

/**
 * Get trending movies
 */
export async function getTrendingMovies(
  page: number = 1,
  timeWindow: 'day' | 'week' = 'week',
  signal?: AbortSignal
): Promise<DiscoveryResponse> {
  const response = await apiFetch(`/movies/trending?page=${page}&timeWindow=${timeWindow}`, {
    signal,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get trending movies' }));
    throw new Error(error.error || 'Failed to get trending movies');
  }

  return response.json();
}

/**
 * Get popular movies
 */
export async function getPopularMovies(
  page: number = 1,
  signal?: AbortSignal
): Promise<DiscoveryResponse> {
  const response = await apiFetch(`/movies/popular?page=${page}`, { signal });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get popular movies' }));
    throw new Error(error.error || 'Failed to get popular movies');
  }

  return response.json();
}

/**
 * Get top rated movies
 */
export async function getTopRatedMovies(
  page: number = 1,
  signal?: AbortSignal
): Promise<DiscoveryResponse> {
  const response = await apiFetch(`/movies/top-rated?page=${page}`, { signal });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get top rated movies' }));
    throw new Error(error.error || 'Failed to get top rated movies');
  }

  return response.json();
}

/**
 * Get now playing movies
 */
export async function getNowPlayingMovies(
  page: number = 1,
  signal?: AbortSignal
): Promise<DiscoveryResponse> {
  const response = await apiFetch(`/movies/now-playing?page=${page}`, { signal });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Failed to get now playing movies' }));
    throw new Error(error.error || 'Failed to get now playing movies');
  }

  return response.json();
}

/**
 * Search movies
 */
export async function searchMovies(
  query: string,
  page: number = 1,
  signal?: AbortSignal
): Promise<DiscoveryResponse> {
  const response = await apiFetch(`/movies/search?q=${encodeURIComponent(query)}&page=${page}`, {
    signal,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to search movies' }));
    throw new Error(error.error || 'Failed to search movies');
  }

  return response.json();
}

/**
 * Search movies by actor or director
 */
export async function searchMoviesByPerson(
  query: string,
  role: 'actor' | 'director',
  signal?: AbortSignal
): Promise<PersonSearchResponse> {
  const response = await apiFetch(
    `/movies/search-person?q=${encodeURIComponent(query)}&role=${role}`,
    { signal }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Failed to search movies by person' }));
    throw new Error(error.error || 'Failed to search movies by person');
  }

  return response.json();
}

// ==================== Reference Data ====================

export interface Genre {
  id: number;
  name: string;
}

export interface LanguageOption {
  code: string;
  name: string;
}

export interface CountryOption {
  code: string;
  name: string;
}

/**
 * Get movie genres
 */
export async function getGenres(signal?: AbortSignal): Promise<Genre[]> {
  const response = await apiFetch('/movies/genres', { signal });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch genres' }));
    throw new Error(error.error || 'Failed to fetch genres');
  }

  const data = await response.json();
  return data.genres;
}

/**
 * Get languages list
 */
export async function getLanguages(signal?: AbortSignal): Promise<LanguageOption[]> {
  const response = await apiFetch('/movies/languages', { signal });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch languages' }));
    throw new Error(error.error || 'Failed to fetch languages');
  }

  const data = await response.json();
  return data.languages;
}

/**
 * Get countries list
 */
export async function getCountries(signal?: AbortSignal): Promise<CountryOption[]> {
  const response = await apiFetch('/movies/countries', { signal });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch countries' }));
    throw new Error(error.error || 'Failed to fetch countries');
  }

  const data = await response.json();
  return data.countries;
}

// ==================== Movie Discover ====================

export interface DiscoverParams {
  genre?: number;
  decade?: number;
  keywords?: string;
  queryText?: string;
  queryScope?: 'title' | 'keywords';
  language?: string;
  country?: string;
  page?: number;
}

/**
 * Discover movies by genre, decade, keywords, language, and/or country
 */
export async function discoverMovies(
  params: DiscoverParams,
  signal?: AbortSignal
): Promise<DiscoveryResponse> {
  const searchParams = new URLSearchParams();
  if (params.genre) searchParams.set('genre', params.genre.toString());
  if (params.decade) searchParams.set('decade', params.decade.toString());
  if (params.keywords) searchParams.set('keywords', params.keywords);
  if (params.queryText) searchParams.set('queryText', params.queryText);
  if (params.queryScope) searchParams.set('queryScope', params.queryScope);
  if (params.language) searchParams.set('language', params.language);
  if (params.country) searchParams.set('country', params.country);
  if (params.page) searchParams.set('page', params.page.toString());

  const response = await apiFetch(`/movies/discover?${searchParams.toString()}`, { signal });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to discover movies' }));
    throw new Error(error.error || 'Failed to discover movies');
  }

  return response.json();
}

/**
 * Get similar movies
 */
export async function getSimilarMovies(
  tmdbId: number,
  options: { limit?: number; includeRecommendations?: boolean } = {}
): Promise<{ results: DiscoveryMovie[]; sourceMovieId: number }> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.includeRecommendations) params.set('includeRecommendations', 'true');

  const queryString = params.toString();
  const url = queryString
    ? `/movies/${tmdbId}/similar?${queryString}`
    : `/movies/${tmdbId}/similar`;

  const response = await apiFetch(url);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get similar movies' }));
    throw new Error(error.error || 'Failed to get similar movies');
  }

  return response.json();
}
